/**
 * ShareConfigDialog - Share Configuration Modal
 *
 * A dialog component for configuring and creating shareable pages.
 * Allows users to set title, data mode (snapshot vs live), access controls,
 * and expiration settings for shared scenario comparisons.
 */

import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Button,
    TextField,
    Typography,
    IconButton,
    FormControl,
    FormControlLabel,
    FormLabel,
    RadioGroup,
    Radio,
    Checkbox,
    InputAdornment,
    Alert,
    CircularProgress,
    Tooltip,
    MenuItem,
    alpha,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import {
    Close as CloseIcon,
    ContentCopy as CopyIcon,
    OpenInNew as OpenInNewIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
    Lock as LockIcon,
    Email as EmailIcon,
    Schedule as ScheduleIcon,
    Check as CheckIcon,
    Info as InfoIcon,
    Description as ProposalIcon,
    CompareArrows as CompareIcon,
} from '@mui/icons-material';
import { useAuth } from '../auth/index.js';
import { createShare, getShareUrl } from '../services/shareService.js';
import { vortxColors, vortxTypography } from '../core/theme.js';

/**
 * ShareConfigDialog Component
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {function} props.onClose - Callback when dialog closes
 * @param {string} props.projectId - Project ID to associate with share
 * @param {string} props.projectName - Project name for default title
 * @param {Array<string>} props.scenarioIds - Array of scenario IDs to share
 * @param {Array<string>} props.scenarioNames - Array of scenario names for display
 * @param {Object} props.comparisonData - Full comparison data for snapshot mode
 */
export default function ShareConfigDialog({
    open,
    onClose,
    projectId,
    projectName,
    scenarioIds,
    scenarioNames,
    comparisonData,
}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { currentUser } = useAuth();

    // Form state
    const [presentationMode, setPresentationMode] = useState('comparison'); // 'comparison' or 'proposal'
    const [proposalScenarioIndices, setProposalScenarioIndices] = useState([]);
    const [customerName, setCustomerName] = useState('');
    const [proposalDealMode, setProposalDealMode] = useState('both'); // 'esa' | 'capex' | 'both'
    const [title, setTitle] = useState('');
    const [dataMode, setDataMode] = useState('snapshot'); // 'snapshot' or 'live'
    const [includeBasicView, setIncludeBasicView] = useState(false);
    const [includeAdvancedView, setIncludeAdvancedView] = useState(false);
    const [requirePassword, setRequirePassword] = useState(false);
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [requireEmail, setRequireEmail] = useState(false);
    const [expirationDays, setExpirationDays] = useState(30);
    const [editableNames, setEditableNames] = useState({});  // { index: 'Custom Name' }
    const [recommendedIndex, setRecommendedIndex] = useState(-1);  // -1 = none
    const [recommendationText, setRecommendationText] = useState('');

    // UI state
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [createdShare, setCreatedShare] = useState(null);
    const [copied, setCopied] = useState(false);

    // Check which scenarios have deal analysis configured (for proposal mode availability)
    const scenarios = comparisonData?.scenarios || [];
    const proposalAvailable = scenarios.some(
        (s) => s?.ui_state?.form?.dealAnalysisConfig?.computedResults
    );
    // Auto-select eligible scenarios when switching to proposal mode
    useEffect(() => {
        if (presentationMode === 'proposal') {
            const eligible = scenarios
                .map((s, i) => s?.ui_state?.form?.dealAnalysisConfig?.computedResults ? i : null)
                .filter(i => i !== null);
            setProposalScenarioIndices(eligible);
        }
    }, [presentationMode]); // eslint-disable-line react-hooks/exhaustive-deps

    // Detect which deal modes are available across selected scenarios
    const selectedHasEsa = proposalScenarioIndices.some(i =>
        !!scenarios[i]?.ui_state?.form?.dealAnalysisConfig?.esaConfig);
    const selectedHasCapex = proposalScenarioIndices.some(i =>
        !!scenarios[i]?.ui_state?.form?.dealAnalysisConfig?.capexConfig);

    // Auto-correct deal mode if selected mode becomes invalid
    useEffect(() => {
        if (proposalDealMode === 'capex' && !selectedHasCapex) setProposalDealMode(selectedHasEsa ? 'esa' : 'both');
        if (proposalDealMode === 'esa' && !selectedHasEsa) setProposalDealMode(selectedHasCapex ? 'capex' : 'both');
        if (proposalDealMode === 'both' && (!selectedHasEsa || !selectedHasCapex)) {
            setProposalDealMode(selectedHasEsa ? 'esa' : 'capex');
        }
    }, [proposalScenarioIndices, selectedHasEsa, selectedHasCapex, proposalDealMode]);

    // Generate default title when dialog opens
    useEffect(() => {
        if (open && !createdShare) {
            const date = new Date().toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
            });
            if (presentationMode === 'proposal') {
                const name = customerName || projectName || 'Project';
                setTitle(`Energy Proposal - ${name} (${date})`);
            } else if (scenarioIds?.length === 1) {
                setTitle(`${scenarioNames?.[0] || 'Scenario'} - ${projectName || 'Project'} (${date})`);
            } else {
                setTitle(`${projectName || 'Project'} - Scenario Comparison (${date})`);
            }
        }
    }, [open, projectName, createdShare, presentationMode, customerName, scenarioIds, scenarioNames]);

    // Reset state when dialog closes
    useEffect(() => {
        if (!open) {
            // Delay reset to allow close animation
            const timer = setTimeout(() => {
                setPresentationMode('comparison');
                setProposalScenarioIndices([]);
                setProposalDealMode('both');
                setCustomerName('');
                setTitle('');
                setDataMode('snapshot');
                setIncludeBasicView(false);
                setIncludeAdvancedView(false);
                setRequirePassword(false);
                setPassword('');
                setShowPassword(false);
                setRequireEmail(false);
                setExpirationDays(30);
                setEditableNames({});
                setRecommendedIndex(-1);
                setRecommendationText('');
                setLoading(false);
                setError(null);
                setCreatedShare(null);
                setCopied(false);
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [open]);

    // Calculate expiration date
    const getExpirationDate = () => {
        if (expirationDays === 0) return null; // Never expires
        const date = new Date();
        date.setDate(date.getDate() + expirationDays);
        return date.toISOString();
    };

    // Handle share creation
    const handleCreate = async () => {
        if (!currentUser) {
            setError('You must be logged in to create a share');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const isProposal = presentationMode === 'proposal';

            // Build allowed_views array
            const allowedViews = isProposal
                ? ['proposal']
                : [
                    ...(includeBasicView ? ['basic'] : []),
                    ...(includeAdvancedView ? ['advanced'] : []),
                ];

            const shareData = {
                type: isProposal ? 'proposal' : 'scenario_comparison',
                project_id: projectId,
                scenario_ids: scenarioIds,
                title: title,
                data_mode: dataMode,
                allowed_views: allowedViews,
                require_password: requirePassword,
                require_email: requireEmail,
                expiration_days: expirationDays === 0 ? null : expirationDays,
            };

            // Add password if required
            if (requirePassword && password) {
                shareData.password = password;
            }

            if (isProposal) {
                // Build proposal config — stored as top-level field on share doc
                // This persists for both snapshot and live modes
                const selectedScenarios = proposalScenarioIndices.map(i => scenarios[i]);
                const selectedNames = proposalScenarioIndices.map(i =>
                    editableNames[i] || scenarioNames?.[i] || scenarios[i]?.name || `Option ${i + 1}`
                );

                const proposalConfig = {
                    customer_name: customerName || null,
                    scenario_names: selectedNames,
                    deal_modes: proposalDealMode,
                    recommended_index: recommendedIndex >= 0 ? proposalScenarioIndices.indexOf(recommendedIndex) : null,
                    recommendation_text: recommendationText || null,
                };

                // Send proposal_config as top-level field (stored on Firestore doc)
                shareData.proposal_config = proposalConfig;

                // Use the selected scenario IDs for live mode
                shareData.scenario_ids = proposalScenarioIndices.map(i => scenarioIds[i]);

                // Snapshot mode: include full scenario data in blob
                if (dataMode === 'snapshot') {
                    shareData.snapshot_data = {
                        scenarios: selectedScenarios,
                        projectName: projectName,
                        proposal_config: proposalConfig,
                    };
                }
            } else if (dataMode === 'snapshot' && comparisonData) {
                shareData.snapshot_data = comparisonData;
            }

            const result = await createShare(shareData, currentUser);
            setCreatedShare(result);
        } catch (err) {
            console.error('Failed to create share:', err);
            setError(err.message || 'Failed to create share. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Handle copy to clipboard
    const handleCopy = async () => {
        if (!createdShare?.id) return;

        const shareType = createdShare.type === 'proposal' ? 'proposal' : 'comparison';
        const url = getShareUrl(createdShare.id, shareType);
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    // Handle preview
    const handlePreview = () => {
        if (!createdShare?.id) return;
        const shareType = createdShare.type === 'proposal' ? 'proposal' : 'comparison';
        const url = getShareUrl(createdShare.id, shareType);
        window.open(url, '_blank');
    };

    // Get the share URL
    const shareUrl = createdShare?.id
        ? getShareUrl(createdShare.id, createdShare.type === 'proposal' ? 'proposal' : 'comparison')
        : '';

    // Validation
    const isProposalMode = presentationMode === 'proposal';
    const hasViewSelected = isProposalMode || includeBasicView || includeAdvancedView;
    const proposalReady = !isProposalMode || proposalScenarioIndices.length > 0;
    const isValid = !loading && hasViewSelected && proposalReady && (!requirePassword || password.length > 0);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
                sx: {
                    borderRadius: isMobile ? 0 : 2,
                    border: isMobile ? 'none' : `1px solid ${alpha(vortxColors.gold, 0.2)}`,
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: `1px solid ${alpha(vortxColors.teal, 0.2)}`,
                    pb: 2,
                }}
            >
                <Box>
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 600,
                            color: vortxColors.gold,
                        }}
                    >
                        {createdShare ? 'Share Created!' : scenarioIds?.length === 1 ? 'Share Scenario' : 'Share Comparison'}
                    </Typography>
                    {!createdShare && (
                        <Typography
                            variant="body2"
                            sx={{ color: 'text.secondary', mt: 0.5 }}
                        >
                            {scenarioIds?.length === 1 ? (scenarioNames?.[0] || '1 scenario') : `${scenarioNames?.length || 0} scenarios selected`}
                        </Typography>
                    )}
                </Box>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: 3, pb: 2 }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {createdShare ? (
                    // Success State
                    <Box>
                        <Alert severity="success" sx={{ mb: 3 }}>
                            Your shareable page has been created successfully.
                        </Alert>

                        <Typography
                            variant="subtitle2"
                            sx={{ mb: 1, color: 'text.secondary' }}
                        >
                            Share URL
                        </Typography>
                        <Box
                            sx={{
                                p: 2,
                                bgcolor: alpha(vortxColors.charcoal, 0.05),
                                borderRadius: 1,
                                border: `1px solid ${alpha(vortxColors.teal, 0.2)}`,
                                mb: 2,
                            }}
                        >
                            <Typography
                                sx={{
                                    fontFamily: vortxTypography.mono,
                                    fontSize: '0.85rem',
                                    wordBreak: 'break-all',
                                    color: 'text.primary',
                                }}
                            >
                                {shareUrl}
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                display: 'flex',
                                gap: 2,
                                flexDirection: isMobile ? 'column' : 'row',
                            }}
                        >
                            <Button
                                variant="contained"
                                startIcon={copied ? <CheckIcon /> : <CopyIcon />}
                                onClick={handleCopy}
                                sx={{
                                    flex: 1,
                                    bgcolor: copied ? 'success.main' : vortxColors.gold,
                                    '&:hover': {
                                        bgcolor: copied ? 'success.dark' : vortxColors.goldHover,
                                    },
                                }}
                            >
                                {copied ? 'Copied!' : 'Copy Link'}
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<OpenInNewIcon />}
                                onClick={handlePreview}
                                sx={{
                                    flex: 1,
                                    borderColor: vortxColors.teal,
                                    color: vortxColors.teal,
                                    '&:hover': {
                                        borderColor: vortxColors.gold,
                                        color: vortxColors.gold,
                                        bgcolor: alpha(vortxColors.gold, 0.05),
                                    },
                                }}
                            >
                                Preview
                            </Button>
                        </Box>

                        {requirePassword && (
                            <Alert
                                severity="info"
                                icon={<LockIcon />}
                                sx={{ mt: 3 }}
                            >
                                This share is password protected. Remember to share the
                                password separately with your recipients.
                            </Alert>
                        )}
                    </Box>
                ) : (
                    // Configuration Form
                    <Box>
                        {/* Presentation Mode */}
                        <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
                            <FormLabel
                                component="legend"
                                sx={{
                                    color: 'text.primary',
                                    fontWeight: 600,
                                    fontSize: '0.875rem',
                                    mb: 1,
                                }}
                            >
                                Presentation Mode
                            </FormLabel>
                            <RadioGroup
                                value={presentationMode}
                                onChange={(e) => setPresentationMode(e.target.value)}
                            >
                                <FormControlLabel
                                    value="comparison"
                                    control={<Radio size="small" />}
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <CompareIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    Technical Comparison
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    sx={{ color: 'text.secondary', display: 'block' }}
                                                >
                                                    Full scenario comparison with charts and metrics
                                                </Typography>
                                            </Box>
                                        </Box>
                                    }
                                    sx={{ alignItems: 'flex-start', mb: 1 }}
                                />
                                <Tooltip
                                    title={!proposalAvailable ? 'No scenarios have deal analysis configured' : ''}
                                    placement="right"
                                >
                                    <FormControlLabel
                                        value="proposal"
                                        control={<Radio size="small" />}
                                        disabled={!proposalAvailable}
                                        label={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <ProposalIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                <Box>
                                                    <Typography variant="body2" sx={{ fontWeight: 500, opacity: proposalAvailable ? 1 : 0.5 }}>
                                                        Customer Proposal
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{ color: 'text.secondary', display: 'block', opacity: proposalAvailable ? 1 : 0.5 }}
                                                    >
                                                        Customer-facing savings summary and agreement terms
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        }
                                        sx={{ alignItems: 'flex-start' }}
                                    />
                                </Tooltip>
                            </RadioGroup>
                        </FormControl>

                        {/* Proposal-specific: Scenario picker + customer name */}
                        {isProposalMode && (
                            <Box sx={{ mb: 3 }}>
                                {scenarios.length > 0 && (
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                                            Scenarios to include
                                        </Typography>
                                        {scenarios.map((s, i) => {
                                            const hasResults = !!s?.ui_state?.form?.dealAnalysisConfig?.computedResults;
                                            const checked = proposalScenarioIndices.includes(i);
                                            const defaultName = scenarioNames?.[i] || s?.name || `Scenario ${i + 1}`;
                                            return (
                                                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                    <Checkbox
                                                        size="small"
                                                        checked={checked}
                                                        disabled={!hasResults}
                                                        onChange={(e) => {
                                                            setProposalScenarioIndices(prev =>
                                                                e.target.checked
                                                                    ? [...prev, i].sort((a, b) => a - b)
                                                                    : prev.filter(idx => idx !== i)
                                                            );
                                                        }}
                                                    />
                                                    <TextField
                                                        size="small"
                                                        value={editableNames[i] ?? ''}
                                                        onChange={(e) => setEditableNames(prev => ({ ...prev, [i]: e.target.value }))}
                                                        placeholder={defaultName}
                                                        disabled={!hasResults || !checked}
                                                        fullWidth
                                                        sx={{
                                                            '& .MuiInputBase-root': { fontSize: '0.875rem' },
                                                            opacity: (hasResults && checked) ? 1 : 0.5,
                                                        }}
                                                        InputProps={{
                                                            sx: { py: 0 },
                                                        }}
                                                    />
                                                    {!hasResults && (
                                                        <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                                                            (no deal analysis)
                                                        </Typography>
                                                    )}
                                                </Box>
                                            );
                                        })}
                                        {proposalScenarioIndices.length === 0 && (
                                            <Typography variant="caption" sx={{ color: 'error.main', mt: 0.5, display: 'block' }}>
                                                Select at least one scenario
                                            </Typography>
                                        )}
                                    </Box>
                                )}

                                <TextField
                                    label="Customer Name"
                                    value={customerName}
                                    onChange={(e) => setCustomerName(e.target.value)}
                                    fullWidth
                                    size="small"
                                    sx={{ mb: 2 }}
                                    helperText="Optional — shown in the proposal header"
                                />

                                {/* Financing Options */}
                                <FormControl component="fieldset" sx={{ width: '100%' }}>
                                    <FormLabel
                                        component="legend"
                                        sx={{
                                            color: 'text.primary',
                                            fontWeight: 500,
                                            fontSize: '0.875rem',
                                            mb: 0.5,
                                        }}
                                    >
                                        Financing Options
                                    </FormLabel>
                                    <RadioGroup
                                        value={proposalDealMode}
                                        onChange={(e) => setProposalDealMode(e.target.value)}
                                    >
                                        <FormControlLabel
                                            value="esa"
                                            control={<Radio size="small" />}
                                            disabled={!selectedHasEsa}
                                            label={
                                                <Typography variant="body2" sx={{ opacity: selectedHasEsa ? 1 : 0.5 }}>
                                                    Energy Service Agreement
                                                </Typography>
                                            }
                                        />
                                        <FormControlLabel
                                            value="capex"
                                            control={<Radio size="small" />}
                                            disabled={!selectedHasCapex}
                                            label={
                                                <Typography variant="body2" sx={{ opacity: selectedHasCapex ? 1 : 0.5 }}>
                                                    Direct Purchase
                                                </Typography>
                                            }
                                        />
                                        <FormControlLabel
                                            value="both"
                                            control={<Radio size="small" />}
                                            disabled={!selectedHasEsa || !selectedHasCapex}
                                            label={
                                                <Box>
                                                    <Typography variant="body2" sx={{ opacity: (selectedHasEsa && selectedHasCapex) ? 1 : 0.5 }}>
                                                        Both — customer can toggle
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                    </RadioGroup>
                                </FormControl>

                                {/* Recommendation */}
                                {proposalScenarioIndices.length > 1 && (
                                    <Box sx={{ mt: 2 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                                            Recommended Option
                                        </Typography>
                                        <TextField
                                            select
                                            value={recommendedIndex}
                                            onChange={(e) => setRecommendedIndex(Number(e.target.value))}
                                            size="small"
                                            fullWidth
                                            sx={{ mb: 2 }}
                                        >
                                            <MenuItem value={-1}>None</MenuItem>
                                            {proposalScenarioIndices.map((idx) => (
                                                <MenuItem key={idx} value={idx}>
                                                    {editableNames[idx] || scenarioNames?.[idx] || scenarios[idx]?.name || `Option ${idx + 1}`}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                        {recommendedIndex >= 0 && (
                                            <TextField
                                                label="Why do you recommend this?"
                                                value={recommendationText}
                                                onChange={(e) => setRecommendationText(e.target.value)}
                                                fullWidth
                                                size="small"
                                                multiline
                                                rows={2}
                                                helperText="Optional — shown as a callout in the proposal"
                                            />
                                        )}
                                    </Box>
                                )}
                            </Box>
                        )}

                        {/* Title */}
                        <TextField
                            label="Title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            fullWidth
                            size="small"
                            sx={{ mb: 3 }}
                            helperText="This title will be displayed on the shared page"
                        />

                        {/* Data Mode */}
                        <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
                            <FormLabel
                                component="legend"
                                sx={{
                                    color: 'text.primary',
                                    fontWeight: 600,
                                    fontSize: '0.875rem',
                                    mb: 1,
                                }}
                            >
                                Data Mode
                            </FormLabel>
                            <RadioGroup
                                value={dataMode}
                                onChange={(e) => setDataMode(e.target.value)}
                            >
                                <FormControlLabel
                                    value="snapshot"
                                    control={<Radio size="small" />}
                                    label={
                                        <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                Snapshot
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                sx={{ color: 'text.secondary', display: 'block' }}
                                            >
                                                Data is captured now and won't change, even if you
                                                update the scenarios later.
                                            </Typography>
                                        </Box>
                                    }
                                    sx={{ alignItems: 'flex-start', mb: 1 }}
                                />
                                <FormControlLabel
                                    value="live"
                                    control={<Radio size="small" />}
                                    label={
                                        <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                Live
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                sx={{ color: 'text.secondary', display: 'block' }}
                                            >
                                                Viewers always see the latest scenario data. Updates
                                                you make will be reflected automatically.
                                            </Typography>
                                        </Box>
                                    }
                                    sx={{ alignItems: 'flex-start' }}
                                />
                            </RadioGroup>
                        </FormControl>

                        {/* Views to Share — hidden for proposal mode */}
                        {!isProposalMode && (
                        <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }} error={!hasViewSelected}>
                            <FormLabel
                                component="legend"
                                sx={{
                                    color: 'text.primary',
                                    fontWeight: 600,
                                    fontSize: '0.875rem',
                                    mb: 1,
                                }}
                            >
                                Views to Share
                            </FormLabel>

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={includeBasicView}
                                        onChange={(e) => setIncludeBasicView(e.target.checked)}
                                        size="small"
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            Basic View
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            sx={{ color: 'text.secondary', display: 'block' }}
                                        >
                                            Summary cards with key metrics and comparisons
                                        </Typography>
                                    </Box>
                                }
                                sx={{ alignItems: 'flex-start', mb: 1 }}
                            />

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={includeAdvancedView}
                                        onChange={(e) => setIncludeAdvancedView(e.target.checked)}
                                        size="small"
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            Advanced View
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            sx={{ color: 'text.secondary', display: 'block' }}
                                        >
                                            Detailed charts, cash flows, and performance data
                                        </Typography>
                                    </Box>
                                }
                                sx={{ alignItems: 'flex-start' }}
                            />

                            {!hasViewSelected && (
                                <Typography
                                    variant="caption"
                                    sx={{ color: 'error.main', mt: 1 }}
                                >
                                    Select at least one view to share
                                </Typography>
                            )}
                        </FormControl>
                        )}

                        {/* Access Settings */}
                        <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
                            <FormLabel
                                component="legend"
                                sx={{
                                    color: 'text.primary',
                                    fontWeight: 600,
                                    fontSize: '0.875rem',
                                    mb: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                }}
                            >
                                <LockIcon sx={{ fontSize: 18 }} />
                                Access Settings
                            </FormLabel>

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={requirePassword}
                                        onChange={(e) => {
                                            setRequirePassword(e.target.checked);
                                            if (!e.target.checked) setPassword('');
                                        }}
                                        size="small"
                                    />
                                }
                                label="Require password"
                                sx={{ mb: 1 }}
                            />

                            {requirePassword && (
                                <Box sx={{ pl: 4, mb: 2 }}>
                                    <TextField
                                        label="Password"
                                        type={showPassword ? 'text' : 'password'}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        fullWidth
                                        size="small"
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={() => setShowPassword(!showPassword)}
                                                        edge="end"
                                                        size="small"
                                                    >
                                                        {showPassword ? (
                                                            <VisibilityOffIcon />
                                                        ) : (
                                                            <VisibilityIcon />
                                                        )}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                        error={requirePassword && !password}
                                        helperText={
                                            requirePassword && !password
                                                ? 'Password is required'
                                                : ''
                                        }
                                    />
                                </Box>
                            )}

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={requireEmail}
                                        onChange={(e) => setRequireEmail(e.target.checked)}
                                        size="small"
                                    />
                                }
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <span>Require email</span>
                                        <Tooltip title="Viewers must enter their email address to access the share. You'll see who viewed your share.">
                                            <InfoIcon
                                                sx={{ fontSize: 16, color: 'text.secondary' }}
                                            />
                                        </Tooltip>
                                    </Box>
                                }
                            />
                        </FormControl>

                        {/* Expiration */}
                        <FormControl component="fieldset" sx={{ width: '100%' }}>
                            <FormLabel
                                component="legend"
                                sx={{
                                    color: 'text.primary',
                                    fontWeight: 600,
                                    fontSize: '0.875rem',
                                    mb: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                }}
                            >
                                <ScheduleIcon sx={{ fontSize: 18 }} />
                                Link Expiration
                            </FormLabel>
                            <RadioGroup
                                row={!isMobile}
                                value={expirationDays}
                                onChange={(e) => setExpirationDays(parseInt(e.target.value, 10))}
                            >
                                <FormControlLabel
                                    value={7}
                                    control={<Radio size="small" />}
                                    label="7 days"
                                />
                                <FormControlLabel
                                    value={30}
                                    control={<Radio size="small" />}
                                    label="30 days"
                                />
                                <FormControlLabel
                                    value={90}
                                    control={<Radio size="small" />}
                                    label="90 days"
                                />
                                <FormControlLabel
                                    value={0}
                                    control={<Radio size="small" />}
                                    label="Never"
                                />
                            </RadioGroup>
                        </FormControl>
                    </Box>
                )}
            </DialogContent>

            <DialogActions
                sx={{
                    px: 3,
                    py: 2,
                    borderTop: `1px solid ${alpha(vortxColors.teal, 0.2)}`,
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: isMobile ? 1 : 0,
                }}
            >
                {createdShare ? (
                    <Button
                        onClick={onClose}
                        variant="contained"
                        fullWidth={isMobile}
                        sx={{
                            bgcolor: vortxColors.gold,
                            '&:hover': { bgcolor: vortxColors.goldHover },
                        }}
                    >
                        Done
                    </Button>
                ) : (
                    <>
                        <Button onClick={onClose} disabled={loading} fullWidth={isMobile}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleCreate}
                            variant="contained"
                            disabled={!isValid}
                            fullWidth={isMobile}
                            sx={{
                                bgcolor: vortxColors.gold,
                                '&:hover': { bgcolor: vortxColors.goldHover },
                                minWidth: 120,
                            }}
                        >
                            {loading ? (
                                <CircularProgress size={24} color="inherit" />
                            ) : (
                                'Create Share'
                            )}
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
}
